/* eslint-disable react-hooks/exhaustive-deps */
import { IGResponse } from "@common/models";
import { IAttraction } from "@common/models/CMS/Attraction";
import { getFilePath, IViewport, MapType } from "@config/appConfig";
import { setLoaderElement } from "@pages/CityInformation";
import { useApolloQuery } from "@service/graphql/Graphql";
import { ATTRACTION_DETAILS } from "@service/graphql/query/Query.Attraction";
import Translate from "@service/Translate";
import { FC, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import Map from "@components/general/Map";
import { renderLoading } from "@pages/BoardingPass";
import { Card } from "@components/general/card/Card";
import { CardContent } from "@components/general/card";
import Title from "@components/general/Title";
import { IAppState } from "@misc/State";
import { useDispatch, useSelector } from "react-redux";
import Config from "@service/Config";
import WidgetContainer from "@components/Widgets/WidgetContainer";
import Navigation from "@components/general/Navigation";
import { getPolygonPath } from "@components/Widgets/mapWidget/MapWidget";
import { LatLngExpression } from "leaflet";
import pulseCircle from '@assets/images/icons/pulse-circle.svg';
import { AppAction, IAppAction } from "@misc/Action";
import { getAirport } from "@utilize/Format";
import { Weather } from "@components/weather";
import { ICity } from "@common/models/CMS";

const AttractionDetails: FC = () => {
    const { tr } = Translate;
    const { id } = useParams<{ id: string }>();
    const dispatch = useDispatch();
    const { small } = Config.get<IViewport>("viewport");
    const { width, headerStyle } = useSelector((state: IAppState) => ({ width: state.windowWidth, headerStyle: state.headerStyle }));
    const { loading, data } = useApolloQuery<IGResponse<IAttraction[]>>(ATTRACTION_DETAILS, { variables: { localizationSet: id } });
    const attractionData = useMemo(() => data?.attraction.length ? data?.attraction[0] : undefined, [data]);

    useEffect(() => {
        // set header transparent mode 
        dispatch<IAppAction>({ type: AppAction.headerStyle, payload: { headerStyle: { ...headerStyle, menuStyle: "Transparent", menuColor: "White" } } });
        return () => {
            dispatch<IAppAction>({ type: AppAction.headerStyle, payload: { headerStyle: { ...headerStyle, menuStyle: "Solid", menuColor: "Default" } } });
        }
    }, [])

    const city: ICity = {
        code: { code: id },
        forecaWeatherId: attractionData?.forecaWeatherId,
        name: getAirport(id).cityName
    }

    return (
        <div className="attraction-page">
            <header className="header-page" style={{ backgroundImage: `url(${getFilePath(attractionData?.cover?.paths[0], true)})` }}>
                <article className="header-article container">
                    <h1 className="title-header">{loading ? setLoaderElement({ height: '30px', width: '50%' }) : `${attractionData?.displayText}`}</h1>
                    <div className="map-header">
                        <article className="map-desc">
                            <h3 className="country">{attractionData?.city?.contentItems[0].country?.contentItems[0].name}</h3>
                            <h3 className="city">{attractionData?.city?.contentItems[0].name}</h3>
                        </article>
                        {!loading &&
                            <div className="map-container">
                                <Map
                                    displayOnlySvg
                                    height="100%"
                                    mode={MapType.Leaflet}
                                    markers={[
                                        {
                                            lat: attractionData?.location?.latitude!,
                                            lng: attractionData?.location?.longitude!,
                                            content: {
                                                contentTitle: attractionData?.address!,
                                                container: attractionData?.displayText!
                                            },
                                            icon: {
                                                iconUrl: pulseCircle,
                                                iconSize: [16, 16],
                                                iconAnchor: [8, 8]
                                            }
                                        }
                                    ]}
                                    leafletPolygons={[
                                        {
                                            paths: getPolygonPath({
                                                points: attractionData?.city?.contentItems[0].country?.contentItems[0].borderCoordinates,
                                            })?.map(x => ({ lat: x.lat, lng: x.lng } as LatLngExpression))!,
                                            options: {
                                                fill: true,
                                                color: "#ffffff",
                                                weight: 1,
                                                fillOpacity: 0.3
                                            }
                                        },
                                        {
                                            paths: getPolygonPath({
                                                points: attractionData?.city?.contentItems[0].borderCoordinates
                                            })?.map(x => ({ lat: x.lat, lng: x.lng } as LatLngExpression))!,
                                            options: {
                                                fill: true,
                                                weight: 0.5,
                                                fillOpacity: 1,
                                                color: "#00a693"
                                            }
                                        }
                                    ]}
                                    areaPoints={[
                                        {
                                            lat: 39.5, lng: 45
                                        },
                                        {
                                            lat: 39.5, lng: 62
                                        },
                                        {
                                            lat: 25, lng: 45
                                        },
                                        {
                                            lat: 25, lng: 62
                                        }
                                    ]}
                                    leafletMapOption={{
                                        initial: {
                                            dragging: false,
                                            zoomControl: false,
                                            scrollWheelZoom: false,
                                        },
                                    }}
                                />
                            </div>
                        }
                    </div>
                </article>
            </header>
            <div className="container">
                <article className="body-article">
                    <section className="panels-container">
                        <section className="geographic-property flex row">
                            <div className="weather-panel-container col-1-2 col-sm-1-1">
                                <Title text={`${tr('weather')}`} size="h2" />
                                <div className="panel weather-container">
                                    <Weather
                                        city={city}
                                        daysCount={width > small ? 5 : 3}
                                    />
                                </div>
                            </div>
                            <div className="map-container col-1-2 col-sm-1-1">
                                <header className="map-header">
                                    <Title text={tr("attraction_location")} size="h2" />
                                    <Navigation
                                        location={{ lat: attractionData?.location?.latitude!, lng: attractionData?.location?.longitude! }}
                                        rounded
                                        color="primary"
                                    />
                                </header>
                                {
                                    loading ? renderLoading().coverElement :
                                        <div className="panel map-panel">
                                            <Map
                                                height="100%"
                                                mode={MapType.Leaflet}
                                                markers={[
                                                    {
                                                        lat: attractionData?.location?.latitude!,
                                                        lng: attractionData?.location?.longitude!,
                                                        content: {
                                                            contentTitle: attractionData?.address!,
                                                            container: attractionData?.displayText!
                                                        },
                                                    }
                                                ]}
                                                fitBound
                                                leafletMapOption={{
                                                    initial: {
                                                        dragging: false,
                                                        zoomControl: false,
                                                        scrollWheelZoom: false,
                                                        zoom: 6,
                                                        center: { lat: attractionData?.location?.latitude || 0, lng: attractionData?.location?.longitude || 0 },
                                                    },
                                                }}
                                            />
                                        </div>
                                }
                            </div>
                        </section>
                        <section className="cards row spa-sm-1 flex">
                            <div className="col-1-4 col-sm-1-2">
                                <Card variant="horizontal" className="panel attraction-card">
                                    <CardContent mediaBackground={{ icon: "icon-clock" }}>
                                        <p className="title m-0">{tr("estimated_visit_duration")}</p>
                                        <h2 className="desc area">{attractionData?.estimatedVisitDuration}</h2>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="col-1-4 col-sm-1-2">
                                <Card variant="horizontal" className="panel attraction-card">
                                    <CardContent mediaBackground={{ icon: "icon-calendar-outline" }}>
                                        <p className="title m-0">{tr("best_visiting_season")}</p>
                                        <h2 className="desc">{attractionData?.bestVisitingSeason}</h2>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="col-1-4 col-sm-1-2">
                                <Card variant="horizontal" className="panel attraction-card">
                                    <CardContent mediaBackground={{ icon: "icon-clock" }}>
                                        <p className="title m-0">{tr("most_visiting_hours")}</p>
                                        <h2 className="desc">{attractionData?.mostVisitingHours}</h2>
                                    </CardContent>
                                </Card>
                            </div>
                            <div className="col-1-4 col-sm-1-2">
                                <Card variant="horizontal" className="panel attraction-card">
                                    <CardContent mediaBackground={{ icon: "icon-area" }}>
                                        <p className="title m-0">{tr("ground_area")}</p>
                                        {attractionData?.groundArea && <h2 className="desc area">{attractionData?.groundArea?.toLocaleString()} m<sup>2</sup></h2>}
                                    </CardContent>
                                </Card>
                            </div>
                        </section>
                    </section>
                    <section className="row" style={{ marginTop: "30px" }} />
                </article>
            </div>
            <WidgetContainer
                generalContainer={attractionData}
            />
        </div>
    );
};

export default AttractionDetails;
